import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiCheck,
  FiCopy,
  FiHeart,
  FiHome,
  FiShield,
} from 'react-icons/fi';

const partnerTowns = [
  { name: 'Gmina Dzierżoniów', image: '/dzierzoniow.png' },
  { name: 'Gmina Miejska Dzierżoniów', image: '/dzierzoniowmiejski.png' },
  { name: 'Gmina Piława Górna', image: '/pilawa-gorna.png' },
  { name: 'Gmina Dobromierz', image: '/dobromierz.png' },
  { name: 'Gmina Bielawa', image: '/bielawa.png' },
  { name: 'Gmina Niemcza', image: '/niemcza.png' },
  { name: 'Gmina Pieszyce', image: '/pieszyce.png' },
  { name: 'Gmina Kobierzyce', image: '/kobierzyce.png' },
  { name: 'Gmina Łagiewniki', image: '/lagiewniki.png' },
];

const carePillars = [
  {
    icon: FiHeart,
    number: '01',
    title: 'Opieka z miłością',
    text: 'Każdy pies otrzymuje od nas bezpieczeństwo, dobre żywienie i codzienną troskę.',
  },
  {
    icon: FiShield,
    number: '02',
    title: 'Zdrowie na pierwszym miejscu',
    text: 'Zapewniamy diagnostykę, leczenie, szczepienia oraz opiekę weterynaryjną.',
  },
  {
    icon: FiHome,
    number: '03',
    title: 'Dom dopasowany na lata',
    text: 'Poznajemy charakter podopiecznych, aby pomóc im znaleźć odpowiednią rodzinę.',
  },
];

export default function Home() {
  const [recentDogs, setRecentDogs] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch('/api/dogs');
        if (!res.ok) throw new Error('API unavailable');
        const data = await res.json();
        setRecentDogs(Array.isArray(data) ? data.slice(0, 6) : []);
      } catch {
        try {
          const fallback = await fetch('/data/dogs.json');
          const data = await fallback.json();
          setRecentDogs(Array.isArray(data) ? data.slice(0, 6) : []);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
    };
    fetchData();
  }, []);

  const copyKrs = async () => {
    try {
      await navigator.clipboard.writeText('0000062210');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error('Unable to copy KRS:', error);
    }
  };

  return (
    <div className="fade-in home-page">
      <section className="home-hero">
        <div className="home-hero__image" style={{ backgroundImage: "url('/hero-animals.jpg')" }} aria-hidden="true" />
        <div className="home-hero__overlay" aria-hidden="true" />
        <div className="site-container home-hero__content">
          <div className="home-hero__copy">
            <span className="eyebrow eyebrow--light">Schronisko AZYL · Dzierżoniów</span>
            <h1>
              Dobra opcja<br />
              <span>to adopcja.</span>
            </h1>
            <p>
              Poznaj zwierzęta, które czekają na bezpieczny dom. Być może jedno z nich
              właśnie czeka na Ciebie.
            </p>
            <div className="hero-actions">
              <Link to="/dogs" className="button button--primary">
                Poznaj podopiecznych <FiArrowRight aria-hidden="true" />
              </Link>
              <Link to="/support" className="button button--ghost">
                Zobacz, jak pomóc
              </Link>
            </div>
          </div>

          <div className="anniversary-card" aria-label="30 lat istnienia schroniska">
            <div className="anniversary-card__number">30</div>
            <div>
              <span className="anniversary-card__label">lat troski o zwierzęta</span>
              <p>1996—2026</p>
            </div>
          </div>
        </div>
        <div className="home-hero__scroll" aria-hidden="true">Przewiń, aby poznać nas bliżej</div>
      </section>

      <section className="section section--dogs">
        <div className="site-container">
          <div className="section-heading section-heading--split">
            <div>
              <span className="eyebrow">Czekają na Ciebie</span>
              <h2>Nasi podopieczni</h2>
            </div>
            <p>Każdy ma inną historię i charakter. Łączy ich jedno — potrzeba bliskości i własnego miejsca.</p>
          </div>

          <div className="dog-grid">
            {recentDogs.map((dog, index) => (
              <Link key={dog.id} to={`/dogs/${dog.id}`} className="dog-card">
                <div className="dog-card__media">
                  {dog.photo ? (
                    <img src={dog.photo} alt={dog.name} loading={index > 2 ? 'lazy' : 'eager'} />
                  ) : (
                    <div className="dog-card__placeholder" aria-hidden="true">🐕</div>
                  )}
                  <span className={`status-pill status-pill--${dog.status || 'available'}`}>
                    {dog.status === 'available' ? 'Do adopcji' : dog.status === 'pending' ? 'W trakcie' : 'Adoptowany'}
                  </span>
                </div>
                <div className="dog-card__body">
                  <div className="dog-card__title-row">
                    <h3>{dog.name}</h3>
                    <span className="dog-card__arrow"><FiArrowRight aria-hidden="true" /></span>
                  </div>
                  <p>{dog.description}</p>
                  {dog.age && <span className="dog-card__meta">{dog.age} {dog.age === 1 ? 'rok' : 'lat'}</span>}
                </div>
              </Link>
            ))}
          </div>

          <div className="section-action">
            <Link to="/dogs" className="text-link">Zobacz wszystkie psy <FiArrowRight aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <section className="section section--care">
        <div className="site-container care-layout">
          <div className="care-intro">
            <span className="eyebrow eyebrow--light">Codziennie w AZYLU</span>
            <h2>Pomoc, która ma swój rytm</h2>
            <p>
              Od pierwszego badania po dzień adopcji — dbamy o każdy etap drogi do lepszego życia.
            </p>
            <Link to="/about" className="text-link text-link--light">Poznaj naszą historię <FiArrowRight aria-hidden="true" /></Link>
          </div>
          <div className="care-list">
            {carePillars.map(({ icon: Icon, number, title, text }) => (
              <article className="care-item" key={title}>
                <span className="care-item__number">{number}</span>
                <Icon className="care-item__icon" aria-hidden="true" />
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section tax-section">
        <div className="site-container tax-card">
          <div className="tax-card__copy">
            <span className="eyebrow">Prosty gest, realna pomoc</span>
            <h2>Przekaż 1,5% podatku</h2>
            <p>
              Twój podatek finansuje leczenie, karmę i bezpieczne warunki dla bezdomnych zwierząt.
            </p>
            <Link to="/support" className="text-link">Dowiedz się więcej <FiArrowRight aria-hidden="true" /></Link>
          </div>
          <div className="krs-box">
            <span>Numer KRS</span>
            <strong>0000062210</strong>
            <button type="button" onClick={copyKrs} className="copy-button" aria-live="polite">
              {copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
              {copied ? 'Skopiowano' : 'Kopiuj numer'}
            </button>
          </div>
        </div>
      </section>

      <section className="section partners-section">
        <div className="site-container">
          <div className="section-heading section-heading--center">
            <span className="eyebrow">Razem dla regionu</span>
            <h2>Gminy partnerskie</h2>
            <p>Wspólnie zapewniamy opiekę bezdomnym zwierzętom z Dolnego Śląska.</p>
          </div>
          <div className="partners-grid">
            {partnerTowns.map((town) => (
              <div key={town.name} className="partner-card">
                <img src={town.image} alt="" loading="lazy" />
                <span>{town.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
